/**
 * 华奋达磁轴 DKS trps ↔ UI binaryData / actionStates
 * 与 main-app hfdDksMapper 固件语义一致，勿与星闪 8 点编码混用。
 */

export { isTank68HeKeyboardType } from "./hfdMagneticAxis";

export type HfdDksBinaryDataItem = {
  value?: number;
  check?: boolean;
  key?: number;
  type?: string;
};

/**
 * trps(8bit) → actionStates[4]
 * - 0：不触发
 * - 1：bits[j]（单次，较少用）
 * - 2：bits[j+4]（公版连续触发，按压/抬起均用此值）
 */
export function hfdMagneticDksTrpsToStates(trps: number): number[] {
  const bits = Number(trps)
    .toString(2)
    .padStart(8, "0")
    .split("")
    .map((b) => Number(b));
  return [0, 1, 2, 3].map((j) => (bits[j] ? 1 : bits[j + 4] ? 2 : 0));
}

/** actionStates[4] → trps(8bit) */
export function hfdMagneticDksStatesToTrps(states: number[]): number {
  const bits = Array.from({ length: 8 }, () => 0);
  for (let j = 0; j < 4; j++) {
    const s = Number(states?.[j] ?? 0);
    if (s === 1) bits[j] = 1;
    else if (s === 2) bits[j + 4] = 1;
  }
  return parseInt(bits.join(""), 2) || 0;
}

/** 磁轴 UI 勾选点 → 固件 actionState（0/1/2），非 tank68he 路径保留 */
export function hfdMagneticDksUiPointToActionState(
  pointIndex: number,
  checked: boolean
): number {
  if (!checked) return 0;
  void pointIndex;
  return 2;
}

/** tank68he 公版：连续连线勾选一律 state=1（bits[0..3]），如 A=[1,1,0,0]→192 */
export function tank68heDksUiPointToActionState(checked: boolean): number {
  return checked ? 1 : 0;
}

/** tank68he 专用：UI binaryData → trps */
export function tank68heDksBinaryDataToTrps(
  binaryData: HfdDksBinaryDataItem[] | undefined
): number {
  const states = [0, 1, 2, 3].map((j) => {
    const item = binaryData?.[j];
    const checked = Boolean(item?.check) || Number(item?.value) === 1;
    return tank68heDksUiPointToActionState(checked);
  });
  return hfdMagneticDksStatesToTrps(states);
}

/** 磁轴 UI binaryData[0..3] → trps */
export function hfdMagneticDksBinaryDataToTrps(
  binaryData: HfdDksBinaryDataItem[] | undefined
): number {
  const states = [0, 1, 2, 3].map((j) => {
    const item = binaryData?.[j];
    const checked = Boolean(item?.check) || Number(item?.value) === 1;
    return hfdMagneticDksUiPointToActionState(j, checked);
  });
  return hfdMagneticDksStatesToTrps(states);
}

/** trps → 写入 template binaryData（磁轴仅使用 0..3，4..7 清零） */
export function hfdMagneticDksTrpsToBinaryData<T extends HfdDksBinaryDataItem>(
  trps: number,
  template: T[]
): T[] {
  const states = hfdMagneticDksTrpsToStates(trps);
  return template.map((entry, i) => {
    if (i >= 4) {
      return { ...entry, value: 0, check: false };
    }
    const active = states[i] > 0;
    return { ...entry, value: active ? 1 : 0, check: active };
  });
}

/** 星闪 DKS：按压 4 点 + 抬起 4 点 → trps */
export function xingshanDksBinaryDataToTrps(
  binaryData: HfdDksBinaryDataItem[] | undefined
): number {
  const trps1: number[] = [];
  const trps2: number[] = [];
  (binaryData ?? []).forEach((item, key) => {
    if (key < 4) trps1.push(Number(item?.value ?? 0));
    else trps2.push(Number(item?.value ?? 0));
  });
  return parseInt([...trps2.reverse(), ...trps1.reverse()].join(""), 2) || 0;
}

/** 星闪 DKS：trps → binaryData */
export function xingshanDksTrpsToBinaryData<T extends HfdDksBinaryDataItem>(
  trps: number,
  template: T[]
): T[] {
  const binaryDataArr = Number(trps)
    .toString(2)
    .padStart(8, "0")
    .split("")
    .map((b) => Number(b));
  const arr1: number[] = [];
  const arr2: number[] = [];
  binaryDataArr.forEach((bit, key) => {
    if (key < 4) arr1.push(bit);
    else arr2.push(bit);
  });
  const arr4 = [...arr2.reverse(), ...arr1.reverse()];
  return template.map((entry, keys) => ({
    ...entry,
    value: arr4[keys] ?? 0,
    check: arr4[keys] === 1,
  }));
}
